import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleDot,
  Clock3,
  GripVertical,
  History,
  Image as ImageIcon,
  ImagePlus,
  Images,
  Library,
  Loader2,
  Phone,
  PlayCircle,
  RefreshCw,
  Search,
  Trash2,
  Upload,
  UploadCloud,
  Video,
  X,
} from "lucide-react";
import GeoapifyPlaceSearch from "./GeoapifyPlaceSearch";
import MediaPickerModal from "../../pages/admin/MediaPickerModal";
import ImageDropUpload from "./ImageDropUpload";
import { Button, iCls, Modal } from "../../pages/admin/_shared";
import RichTextEditor from "./RichTextEditor";
import LocationReviewStep from "./locations/LocationReviewStep";
import { STATUS_META } from "./locations/workflow";
import { errorsForStep, focusFirstError, issueCountByStep } from "./locations/validation";
import { getMarkerPreviewSpec, MAP_ICON_OPTIONS } from "../../lib/locationMarkerIcons";
import { resolveAssetDisplayUrl } from "../../lib/assetUrl";
import { normalizeImageFileForUpload } from "../../lib/normalizeImageFile";

function cls(...parts) {
  return parts.filter(Boolean).join(" ");
}

function slugifyLite(text = "") {
  return String(text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

function onlyDigits(value = "") {
  return String(value || "").replace(/\D/g, "");
}

function formatMoneyInput(value = "") {
  const digits = onlyDigits(value);
  if (!digits) return "";
  return Number(digits).toLocaleString("vi-VN");
}

function parsePriceRange(value = "") {
  const [minRaw = "", maxRaw = ""] = String(value || "").split("-");
  return {
    priceMin: Number(onlyDigits(minRaw)) || 0,
    priceMax: Number(onlyDigits(maxRaw)) || 0,
  };
}

function formatPriceRange(min, max) {
  const a = Number(min || 0);
  const b = Number(max || 0);

  if (!a && !b) return "0";
  if (a && b) return `${a.toLocaleString("vi-VN")} - ${b.toLocaleString("vi-VN")}`;
  return `${(a || b).toLocaleString("vi-VN")}`;
}

function toTikTokEmbedUrl(input = "") {
  const raw = String(input || "").trim();
  if (!raw) return "";

  // If user pasted an embed URL already, normalize a bit.
  if (/tiktok\.com\/embed\//i.test(raw)) {
    // Keep query params as-is; iframe src builder can append autoplay/muted.
    return raw;
  }

  // Normalize URLs without protocol
  const normalized = /^https?:\/\//i.test(raw) ? raw : `https://${raw.replace(/^\/+/, "")}`;

  // Extract video id from common TikTok URL variants:
  // - https://www.tiktok.com/@user/video/<id>
  // - https://m.tiktok.com/v/<id>.html
  // - https://www.tiktok.com/t/<shortcode> (cannot be resolved offline)
  // - any URL that includes .../video/<id>...
  const m =
    normalized.match(/\/video\/(\d{8,})/i) ||
    normalized.match(/\/v\/(\d{8,})/i) ||
    normalized.match(/tiktok\.com\/embed\/v2\/(\d{8,})/i) ||
    normalized.match(/tiktok\.com\/embed\/(\d{8,})/i);
  if (m && m[1]) return `https://www.tiktok.com/embed/v2/${m[1]}/`;

  return "";
}

function tiktokPreviewSrc(embedUrl = "") {
  const u = String(embedUrl || "").trim();
  if (!u) return "";
  if (/[?&]autoplay=/i.test(u)) return u;
  const sep = u.includes("?") ? "&" : "?";
  return `${u}${sep}autoplay=0&muted=1`;
}

/** Một nhóm trường: heading 18px + mô tả + divider phía trên — không lồng card. */
function Section({ title, desc, right, children, first = false }) {
  return (
    <section className={cls(!first && "border-t border-admin-border pt-6")}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold leading-6 text-admin-text">{title}</h3>
          {desc ? <p className="mt-1 text-xs leading-5 text-admin-textMuted">{desc}</p> : null}
        </div>
        {right ? <div className="shrink-0">{right}</div> : null}
      </div>
      {children}
    </section>
  );
}

/** Label 14px · helper 12px · lỗi hiển thị ngay dưới ô. `data-field` để focus/nhảy tới ô lỗi. */
function FormField({ label, required, optional, hint, error, name, counter, className, children }) {
  return (
    <div data-field={name} data-invalid={error ? "true" : undefined} className={className}>
      {label ? (
        <div className="mb-2 flex items-baseline justify-between gap-2">
          <label className="text-sm font-medium text-admin-text">
            {label}
            {required ? (
              <span className="ml-0.5 text-admin-danger" aria-hidden>
                *
              </span>
            ) : null}
            {optional ? <span className="ml-1.5 text-xs font-normal text-admin-textMuted">Tuỳ chọn</span> : null}
          </label>
          {counter || null}
        </div>
      ) : null}
      {children}
      {error ? (
        <p className="mt-1.5 flex items-start gap-1 text-xs font-medium text-admin-danger">
          <AlertCircle size={13} className="mt-px shrink-0" />
          {error}
        </p>
      ) : hint ? (
        <p className="mt-1.5 text-xs leading-5 text-admin-textMuted">{hint}</p>
      ) : null}
    </div>
  );
}

/** Đếm ký tự: vượt max → đỏ, chưa tới min → vàng. */
function CharCounter({ count, max, min }) {
  const tone =
    max && count > max ? "text-admin-danger" : min && count > 0 && count < min ? "text-admin-warning" : "text-admin-textMuted";
  return (
    <span className={cls("shrink-0 text-xs tabular-nums", tone)}>
      {count}
      {max ? `/${max}` : min ? ` · tối thiểu ${min}` : ""}
    </span>
  );
}

const inputCls = (error, extra = "") =>
  cls(iCls, error && "border-admin-danger hover:border-admin-danger focus:border-admin-danger focus:ring-admin-danger/10", extra);

function IconBtn({ icon: Icon, label, danger, disabled, onClick, className }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={cls(
        "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition disabled:cursor-not-allowed disabled:opacity-35",
        danger
          ? "text-admin-textMuted hover:bg-admin-danger/10 hover:text-admin-danger"
          : "text-admin-textMuted hover:bg-admin-muted hover:text-admin-text",
        className
      )}
    >
      <Icon size={15} />
    </button>
  );
}

function ImagePreview({ src, fallbackSrc, emptyText = "Chưa chọn ảnh", heightCls = "h-36" }) {
  const [activeSrc, setActiveSrc] = useState(src || "");
  const [failed, setFailed] = useState(false);

  React.useEffect(() => {
    setActiveSrc(src || "");
    setFailed(false);
  }, [src]);

  if (!activeSrc || failed) {
    return (
      <div
        className={cls(
          "flex flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-admin-border bg-admin-canvas text-xs text-admin-textMuted",
          heightCls
        )}
      >
        <ImageIcon size={20} className="opacity-60" />
        {emptyText}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-admin-border bg-admin-canvas">
      <img
        src={activeSrc}
        alt=""
        className={cls("w-full object-cover", heightCls)}
        onError={() => {
          if (fallbackSrc && activeSrc !== fallbackSrc) {
            setActiveSrc(fallbackSrc);
            return;
          }
          setFailed(true);
        }}
      />
    </div>
  );
}

function moveItem(arr, from, to) {
  const next = [...(arr || [])];
  if (from < 0 || from >= next.length || to < 0 || to >= next.length) return next;
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
}

/**
 * Kéo thả sắp xếp (HTML5 DnD). `withHandle`: chỉ kéo được khi giữ tay nắm (tránh xung đột với ô nhập trong hàng).
 */
function useDragReorder(onReorder, { withHandle = false } = {}) {
  const [dragIdx, setDragIdx] = useState(-1);
  const [overIdx, setOverIdx] = useState(-1);
  const [armedIdx, setArmedIdx] = useState(-1);

  const reset = () => {
    setDragIdx(-1);
    setOverIdx(-1);
    setArmedIdx(-1);
  };

  const itemProps = (idx) => ({
    draggable: withHandle ? armedIdx === idx : true,
    onDragStart: (e) => {
      setDragIdx(idx);
      try {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", String(idx));
      } catch {
        // ignore
      }
    },
    onDragOver: (e) => {
      if (dragIdx < 0) return;
      e.preventDefault();
      if (overIdx !== idx) setOverIdx(idx);
    },
    onDrop: (e) => {
      e.preventDefault();
      if (dragIdx >= 0 && dragIdx !== idx) onReorder(dragIdx, idx);
      reset();
    },
    onDragEnd: reset,
  });

  const handleProps = (idx) => ({
    onMouseDown: () => setArmedIdx(idx),
    onMouseUp: () => setArmedIdx(-1),
    onTouchStart: () => setArmedIdx(idx),
  });

  return { itemProps, handleProps, dragIdx, overIdx };
}

/** Chọn danh mục: tìm kiếm + mô tả loại hình con của từng danh mục. */
function CategoryPicker({ categories, value, onChange, invalid, describe }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) setQ("");
  }, [open]);

  const selected = (categories || []).find((c) => String(c?._id) === String(value));
  const norm = (s) => slugifyLite(s).replace(/-/g, " ");
  const list = (categories || []).filter((c) => !q.trim() || norm(c?.name).includes(norm(q)) || norm(describe(c)).includes(norm(q)));

  return (
    <div
      className="relative"
      ref={ref}
      onKeyDown={(e) => {
        if (e.key === "Escape" && open) {
          e.stopPropagation();
          setOpen(false);
        }
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={cls(inputCls(invalid), "flex items-center gap-2 text-left")}
      >
        <span className={cls("min-w-0 flex-1 truncate", !selected && "text-admin-textMuted/70")}>
          {selected ? selected.name : "Chọn danh mục"}
        </span>
        <ChevronDown size={16} className={cls("shrink-0 text-admin-textMuted transition", open && "rotate-180")} />
      </button>

      {open ? (
        <div className="absolute left-0 right-0 top-full z-40 mt-1 overflow-hidden rounded-xl border border-admin-border bg-admin-surface shadow-soft-xl">
          <div className="border-b border-admin-border p-2">
            <div className="relative">
              <Search size={14} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-admin-textMuted" />
              <input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (list[0]) {
                      onChange(String(list[0]._id));
                      setOpen(false);
                    }
                  }
                }}
                placeholder="Tìm danh mục…"
                className={cls(iCls, "h-9 pl-8")}
              />
            </div>
          </div>
          <ul role="listbox" className="max-h-64 overflow-y-auto p-1">
            {list.length === 0 ? (
              <li className="px-3 py-3 text-sm text-admin-textMuted">Không có danh mục phù hợp</li>
            ) : (
              list.map((c) => {
                const active = String(c._id) === String(value);
                const sub = describe(c);
                return (
                  <li key={c._id}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={active}
                      onClick={() => {
                        onChange(String(c._id));
                        setOpen(false);
                      }}
                      className={cls(
                        "flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left transition",
                        active ? "bg-admin-primaryLight" : "hover:bg-admin-muted"
                      )}
                    >
                      <span className="min-w-0 flex-1">
                        <span className={cls("block text-sm", active ? "font-semibold text-admin-primary" : "font-medium text-admin-text")}>{c.name}</span>
                        {sub ? <span className="mt-0.5 block truncate text-xs text-admin-textMuted">{sub}</span> : null}
                      </span>
                      {active ? <Check size={15} className="mt-0.5 shrink-0 text-admin-primary" /> : null}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

const SUBTYPE_GROUPS = [
  {
    main: "du-lich-van-hoa",
    label: "ĐIỂM VĂN HOÁ",
    items: [
      { value: "du-lich", label: "Du lịch" },
      { value: "chua", label: "Chùa" },
      { value: "nha-tho", label: "Nhà thờ" },
      { value: "di-tich", label: "Di tích" },
    ],
  },
  {
    main: "am-thuc-local",
    label: "ĂN/ UỐNG LOCAL",
    items: [
      { value: "ca-phe-local", label: "Cà phê local" },
      { value: "ca-phe-co-view", label: "Cà phê có view" },
      { value: "quan-lau-nuong", label: "Lẩu nướng" },
      { value: "quan-com-gia-dinh", label: "Cơm gia đình" },
      { value: "quan-an-no", label: "Ăn no" },
      { value: "quan-an-vat", label: "Ăn vặt" },
    ],
  },
  {
    main: "homestay-khach-san",
    label: "HOMESTAY",
    items: [
      { value: "home-co-view", label: "Home có view" },
      { value: "home-tien-nghi", label: "Home tiện nghi" },
      { value: "khach-san", label: "Khách sạn" },
    ],
  },
];


function slugifySubtype(v) {
  return String(v || "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function coerceSubtypeValue(raw, allowedValues) {
  const s = String(raw || "").trim();
  if (!s) return "auto";
  const allow = (v) => (allowedValues instanceof Set ? allowedValues.has(v) : false);
  const direct = allow(s) ? s : "";
  if (direct) return direct;
  const slug = slugifySubtype(s);
  // If the stored value is a label (legacy), try to map to one of the allowed values.
  if (allow(slug)) return slug;
  return "auto";
}

function canonicalMainCategorySlug(raw) {
  const s = String(raw || "").trim();
  if (!s) return "";
  // Support both legacy slugs and newer short slugs from DB/UI.
  if (s === "du-lich-van-hoa" || s === "diem-van-hoa" || s === "diem-van-hoa-local") return "du-lich-van-hoa";
  if (
    s === "am-thuc-local" ||
    s === "am-thuc-dia-phuong" ||
    s === "am-thuc" ||
    s === "an-uong-local" ||
    s === "an-uong"
  )
    return "am-thuc-local";
  if (s === "homestay-khach-san" || s === "homestay") return "homestay-khach-san";
  return s;
}

function mapIconFromSubtype(mainCategorySlug, subtypeValue) {
  const main = canonicalMainCategorySlug(mainCategorySlug);
  const s = String(subtypeValue || "").trim();
  if (!s || s === "auto") return "all";

  if (main === "du-lich-van-hoa") {
    if (s === "du-lich") return "culture";
    if (s === "chua") return "temple";
    if (s === "nha-tho") return "church";
    if (s === "di-tich") return "culture";
    return "culture";
  }
  if (main === "am-thuc-local") {
    if (s === "ca-phe-local" || s === "ca-phe-co-view") return "coffee";
    return "food";
  }
  if (main === "homestay-khach-san") {
    return "stay";
  }
  return "all";
}

export default function LocationEditorModal({
  open,
  onClose,
  title,
  TABS,
  tab,
  setTab,
  form,
  setForm,
  categories,
  saving,
  saveNote,
  modalMode,
  isFirstStep,
  isLastStep,
  goPrevStep,
  goNextStep,
  handleSave,
  draftMedia,
  setDraftMedia,
  uploading,
  uploadCoverDraft,
  uploadVideosDraft,
  videoDraft,
  setVideoDraft,
  MapPicker,
  round6,
  toast,
  uploadOneToDraft,
  formErrors = {},
  onClearError,
  onSetErrors,
  validation = { errors: {}, blockers: [], hints: [] },
  dirty = false,
  lastAutosave = null,
  draftBanner = null,
  onSubmit,
  publishAs = "published",
  alreadyPublished = false,
}) {
  // Sửa nội dung một ô thì xoá lỗi của ô đó
  const prevFormRef = useRef(form);
  useEffect(() => {
    const prev = prevFormRef.current;
    prevFormRef.current = form;
    if (!onClearError) return;
    Object.keys(formErrors).forEach((k) => {
      if (formErrors[k] && prev?.[k] !== form?.[k]) onClearError(k);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const MAX_VIDEO_MB = 30;
  const MAX_VIDEO_BYTES = MAX_VIDEO_MB * 1024 * 1024;
  const CAPTION_MAX_LENGTH = 500;
  const canAcceptVideoFile = (file) => {
    if (!file) return false;
    const size = Number(file.size || 0);
    if (size > MAX_VIDEO_BYTES) {
      toast?.(`Video quá lớn. Giới hạn tối đa ${MAX_VIDEO_MB}MB.`, "error");
      return false;
    }
    return true;
  };

  const setter = (key) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((p) => ({ ...p, [key]: val }));
  };

  const setTikTokUrl = (nextUrl) => {
    setForm((p) => {
      const embed = toTikTokEmbedUrl(nextUrl);
      return {
        ...p,
        tiktokUrl: nextUrl,
        // Auto-fill embed when we can parse video id
        ...(embed ? { tiktokEmbed: embed } : null),
      };
    });
  };

  const setNameAndMaybeSlug = (nextName) => {
    setForm((p) => {
      const next = { ...p, name: nextName };
      const prevSlug = String(p.slug || "").trim();
      const prevAuto = slugifyLite(p.name || "");
      const shouldAuto = !prevSlug || prevSlug === prevAuto;
      if (shouldAuto) next.slug = slugifyLite(nextName);
      return next;
    });
  };

  const [collectionPickerOpen, setCollectionPickerOpen] = useState(false);
  const [collectionPickerIdx, setCollectionPickerIdx] = useState(-1);
  const [collectionUploadingIdx] = useState(-1);
  const [heroPickerOpen, setHeroPickerOpen] = useState(false);

  // ImageDropUpload modal states
  const [dropCoverOpen, setDropCoverOpen] = useState(false);
  const [dropHeroOpen, setDropHeroOpen] = useState(false);
  const [dropContentIdx, setDropContentIdx] = useState(-1);
  const [geoQuery, setGeoQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    if (tab !== "location") return;
    // Keep the search box editable and independent from `form.name`.
    setGeoQuery(String(form.name || ""));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab]);

  const uploadCollectionImageDraft = async (file, idx) => {
    if (!file) return;
    // New: don't upload to draft. Keep local preview; upload on Save.
    const normalized = await normalizeImageFileForUpload(file);
    const objectUrl = URL.createObjectURL(normalized);
    const asset = {
      key: "",
      url: objectUrl,
      mimeType: normalized.type || "",
      size: normalized.size || 0,
      alt: "",
      __localFile: normalized,
      __localName: normalized.name || "",
    };
    setForm((p) => {
      const next = [...(p.mediaBlocks || [])];
      if (!next[idx]) return p;
      next[idx] = { ...next[idx], type: "image", image: asset };
      return { ...p, mediaBlocks: next };
    });
  };

  const uploadHeroImageDraft = async (file) => {
    if (!file) return;
    // New: don't upload to draft. Keep local preview; upload on Save.
    const normalized = await normalizeImageFileForUpload(file);
    const objectUrl = URL.createObjectURL(normalized);
    const asset = {
      key: "",
      url: objectUrl,
      mimeType: normalized.type || "",
      size: normalized.size || 0,
      alt: "",
      __localFile: normalized,
      __localName: normalized.name || "",
    };
    setForm((p) => {
      const prev = Array.isArray(p.heroGallery) ? p.heroGallery : [];
      const dedupeKey = `${asset.__localName}-${asset.size}-${asset.mimeType}`;
      if (prev.some((x) => x?.__localName && `${x.__localName}-${x.size}-${x.mimeType}` === dedupeKey)) return p;
      return { ...p, heroGallery: [...prev, asset] };
    });
  };

  const addCollectionItem = () => {
    setForm((p) => ({
      ...p,
      mediaBlocks: [...(p.mediaBlocks || []), { type: "image", caption: "", image: null }],
    }));
  };

  const mediaCount = (form.mediaBlocks || []).length;
  const hasCover = Boolean(draftMedia?.cover?.url);
  const selectedCategorySlug = useMemo(() => {
    const id = String(form.category || "").trim();
    if (!id) return "";
    const found = (categories || []).find((c) => String(c?._id || "") === id);
    return String(found?.slug || "").trim();
  }, [categories, form.category]);
  const allowedSubTypeValues = useMemo(() => {
    const main = canonicalMainCategorySlug(selectedCategorySlug);
    const set = new Set(["auto"]);
    const group = SUBTYPE_GROUPS.find((g) => g.main === main);
    (group?.items || []).forEach((it) => set.add(it.value));
    return set;
  }, [selectedCategorySlug]);

  const selectedSubTypeValue = useMemo(
    () => coerceSubtypeValue(form.categorySlug, allowedSubTypeValues),
    [form.categorySlug, allowedSubTypeValues]
  );
  const mapIconPreview = useMemo(() => {
    const previewCategorySlug = String(form.mapIcon || "all") === "all" ? selectedCategorySlug : selectedSubTypeValue;
    try {
      return getMarkerPreviewSpec(form.mapIcon, null, previewCategorySlug, "color");
    } catch {
      return null;
    }
  }, [form.mapIcon, selectedCategorySlug, selectedSubTypeValue]);
  const mapIconLabel = useMemo(() => {
    const found = MAP_ICON_OPTIONS.find((o) => o.value === form.mapIcon);
    return found?.label || "";
  }, [form.mapIcon]);

  const customMapIconUrl = String(form?.mapIconAsset?.url || "").trim();

  // Keep categorySlug normalized + auto-sync mapIcon from subtype.
  useEffect(() => {
    if (!open) return;
    setForm((p) => {
      const subtype = coerceSubtypeValue(p.categorySlug, allowedSubTypeValues);
      const nextCategorySlug = subtype === "auto" ? "" : subtype;
      const nextMapIcon = mapIconFromSubtype(selectedCategorySlug, subtype);
      const needCategory = String(p.categorySlug || "") !== String(nextCategorySlug || "");
      const needIcon = String(p.mapIcon || "all") !== String(nextMapIcon || "all");
      if (!needCategory && !needIcon) return p;
      return {
        ...p,
        ...(needCategory ? { categorySlug: nextCategorySlug } : null),
        ...(needIcon ? { mapIcon: nextMapIcon } : null),
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedSubTypeValue, allowedSubTypeValues, selectedCategorySlug]);

  const [coverPickerOpen, setCoverPickerOpen] = useState(false);

  const mainSlug = canonicalMainCategorySlug(selectedCategorySlug);
  const subtypeGroup = SUBTYPE_GROUPS.find((g) => g.main === mainSlug) || null;
  const describeCategory = useCallback((c) => {
    const g = SUBTYPE_GROUPS.find((x) => x.main === canonicalMainCategorySlug(c?.slug));
    return g ? g.items.map((it) => it.label).join(", ") : "";
  }, []);
  const priceLabel =
    mainSlug === "du-lich-van-hoa" ? "Giá vé (VNĐ)" : mainSlug === "homestay-khach-san" ? "Giá phòng / đêm (VNĐ)" : "Khoảng giá (VNĐ)";
  const priceHint =
    mainSlug === "du-lich-van-hoa"
      ? "Để 0 nếu miễn phí. Nhập khoảng dạng 20.000 - 50.000."
      : "Nhập một mức giá hoặc khoảng dạng 100.000 - 300.000.";
  // Homestay/khách sạn thường không có giờ mở cửa — chỉ hiện khi đã có dữ liệu cũ
  const showHours = mainSlug !== "homestay-khach-san" || Boolean(form.openTime || form.closeTime);

  const heroDrag = useDragReorder((from, to) => setForm((p) => ({ ...p, heroGallery: moveItem(p.heroGallery, from, to) })));
  const blockDrag = useDragReorder((from, to) => setForm((p) => ({ ...p, mediaBlocks: moveItem(p.mediaBlocks, from, to) })), {
    withHandle: true,
  });
  const videoDrag = useDragReorder((from, to) => setDraftMedia((p) => ({ ...p, videos: moveItem(p.videos, from, to) })), {
    withHandle: true,
  });

  const stepIndex = Math.max(0, TABS.findIndex((t) => t.id === tab));
  const statusMeta = STATUS_META[form.status] || STATUS_META.draft;
  const descLen = String(form.description || "").replace(/<[^>]*>/g, "").trim().length;
  const subtypeLabel = subtypeGroup?.items.find((it) => it.value === selectedSubTypeValue)?.label || "";

  const scrollRef = useRef(null);
  const scrollToTopSoon = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const run = () => {
      const targets = [];
      try {
        targets.push(el);
        let cur = el;
        while (cur && cur.parentElement) {
          cur = cur.parentElement;
          if (!(cur instanceof HTMLElement)) continue;
          const style = window.getComputedStyle(cur);
          const oy = style?.overflowY;
          const canScroll =
            (oy === "auto" || oy === "scroll") && cur.scrollHeight > cur.clientHeight + 1;
          if (canScroll) targets.push(cur);
        }
        if (document.scrollingElement) targets.push(document.scrollingElement);
      } catch {
        // ignore
      }

      for (const t of targets) {
        try {
          if (!t) continue;
          if (typeof t.scrollTo === "function") t.scrollTo({ top: 0, left: 0, behavior: "auto" });
          else t.scrollTop = 0;
        } catch {
          // ignore
        }
      }
    };
    window.requestAnimationFrame(run);
    window.setTimeout(run, 0);
    window.setTimeout(run, 60);
    window.setTimeout(run, 140);
  }, []);

  useEffect(() => {
    if (!open) return;
    scrollToTopSoon();
  }, [open, tab, scrollToTopSoon]);

  const handleTabClick = useCallback(
    (id) => {
      setTab(id);
      scrollToTopSoon();
    },
    [scrollToTopSoon, setTab]
  );

  const goToStep = useCallback(
    (step, field) => {
      setTab(step);
      scrollToTopSoon();
      if (field) focusFirstError([field]);
    },
    [setTab, scrollToTopSoon]
  );

  // "Tiếp tục": chỉ đi tiếp khi các trường tối thiểu của bước hiện tại hợp lệ; lỗi hiện ngay dưới ô + focus ô đầu tiên
  const handleNext = useCallback(() => {
    const stepErrors = errorsForStep(validation.errors, tab);
    const keys = Object.keys(stepErrors);
    if (keys.length) {
      onSetErrors?.((prev) => ({ ...prev, ...stepErrors }));
      focusFirstError(keys);
      toast?.(`Còn ${keys.length} trường cần sửa trước khi tiếp tục`, "error");
      return;
    }
    goNextStep?.();
    scrollToTopSoon();
  }, [goNextStep, scrollToTopSoon, validation.errors, tab, onSetErrors, toast]);

  // Có lỗi mới được gắn vào ô → focus ô lỗi đầu tiên
  const errorKeysSig = Object.keys(formErrors).filter((k) => formErrors[k]).join(",");
  useEffect(() => {
    if (errorKeysSig) focusFirstError(errorKeysSig.split(","));
  }, [errorKeysSig]);

  const stepBadges = issueCountByStep(formErrors);
  const totalIssues = Object.values(stepBadges).reduce((a, b) => a + b, 0);
  const primaryLabel = alreadyPublished ? "Lưu thay đổi" : publishAs === "published" ? "Xuất bản" : publishAs === "review" ? "Gửi duyệt" : "Lưu";
  const primaryStatus = alreadyPublished ? "published" : publishAs === "save" ? "draft" : publishAs;
  const canPublishNow = validation.blockers.length === 0 && Object.keys(validation.errors).length === 0;
  const primaryBlocked = (publishAs === "published" || alreadyPublished) && !canPublishNow;

  const handlePrev = useCallback(() => {
    goPrevStep?.();
    scrollToTopSoon();
  }, [goPrevStep, scrollToTopSoon]);

  const coverSrc = draftMedia?.cover?.url ? resolveAssetDisplayUrl(draftMedia.cover.url, draftMedia.cover.key) : "";
  const coverFallback = typeof draftMedia?.cover?.url === "string" ? draftMedia.cover.url.trim() : "";
  const heroList = Array.isArray(form.heroGallery) ? form.heroGallery : [];
  const videoList = draftMedia?.videos || [];

  const footer = (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
      <div className="min-w-0 flex-1 text-xs leading-5 text-admin-textMuted">
        {saveNote ? (
          <span className="font-medium text-admin-text">{saveNote}</span>
        ) : isLastStep && primaryBlocked ? (
          <span className="font-medium text-admin-warning">Chưa đủ điều kiện xuất bản — bạn vẫn có thể lưu nháp.</span>
        ) : (
          <span>
            Bước {stepIndex + 1}/{TABS.length} · {TABS[stepIndex]?.label}
          </span>
        )}
      </div>
      <div className="flex gap-2 [&>*]:flex-1 sm:[&>*]:flex-none">
        {!isFirstStep ? (
          <Button variant="secondary" onClick={handlePrev} icon={ChevronLeft}>
            Quay lại
          </Button>
        ) : null}
        {!alreadyPublished ? (
          <Button variant="secondary" disabled={saving} onClick={() => onSubmit?.("draft")}>
            Lưu nháp
          </Button>
        ) : null}
        {isLastStep ? (
          <Button
            type="submit"
            form="loc-editor-form"
            variant="primary"
            loading={saving}
            disabled={primaryBlocked}
            title={primaryBlocked ? "Chưa đủ điều kiện — xem danh sách còn thiếu" : undefined}
          >
            {saving ? "Đang lưu…" : primaryLabel}
          </Button>
        ) : (
          <Button variant="primary" onClick={handleNext}>
            Tiếp tục
            <ChevronRight size={16} />
          </Button>
        )}
      </div>
    </div>
  );

  const tree = (
    <>
      <Modal
        open={open}
        onClose={onClose}
        maxWidthClass="max-w-4xl"
        panelClassName="loc-editor !max-h-[min(94vh,960px)]"
        footer={footer}
        title={
          <div className="min-w-0 pr-2">
            <p className="text-xs font-medium text-admin-textMuted">{title}</p>
            <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
              <h2 className="min-w-0 truncate text-lg font-semibold leading-7 text-admin-text">
                {form.name?.trim() || "Địa điểm chưa đặt tên"}
              </h2>
              <span className={cls("inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ring-1", statusMeta.badge)}>
                <span className={cls("h-1.5 w-1.5 rounded-full", statusMeta.dot)} />
                {statusMeta.label}
              </span>
              <span className={cls("inline-flex items-center gap-1.5 text-xs", dirty ? "text-admin-warning" : "text-admin-textMuted")}>
                {dirty ? <CircleDot size={12} /> : <Check size={12} />}
                {dirty ? "Chưa lưu thay đổi" : "Đã lưu"}
                {lastAutosave ? (
                  <span className="text-admin-textMuted">
                    · tự lưu nháp {new Date(lastAutosave).toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                ) : null}
              </span>
            </div>
          </div>
        }
      >
        {/* Thanh tiến trình 4 bước — dính trên cùng khi cuộn */}
        <nav
          aria-label="Các bước"
          className="sticky -top-5 z-30 -mx-5 -mt-5 mb-6 border-b border-admin-border bg-admin-surface/95 px-5 pb-3 pt-3 backdrop-blur sm:-mx-6 sm:px-6"
        >
          <ol className="grid grid-cols-4 gap-2 sm:gap-3">
            {TABS.map((t, index) => {
              const active = tab === t.id;
              const done = index < stepIndex;
              const issues = stepBadges[t.id] || 0;
              return (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => handleTabClick(t.id)}
                    aria-current={active ? "step" : undefined}
                    className="group block w-full text-left"
                  >
                    <span
                      className={cls(
                        "block h-1 rounded-full transition-colors",
                        issues ? "bg-admin-danger" : active || done ? "bg-admin-primary" : "bg-admin-border group-hover:bg-admin-textMuted/40"
                      )}
                    />
                    <span className="mt-2 flex min-w-0 items-center gap-1.5">
                      <span
                        className={cls(
                          "flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold",
                          issues
                            ? "bg-admin-danger text-white"
                            : active
                              ? "bg-admin-primary text-white"
                              : done
                                ? "bg-admin-primaryLight text-admin-primary"
                                : "bg-admin-muted text-admin-textMuted"
                        )}
                        aria-label={issues ? `${issues} lỗi` : undefined}
                      >
                        {issues ? issues : done ? <Check size={12} /> : index + 1}
                      </span>
                      <span
                        className={cls(
                          "truncate text-xs sm:text-sm",
                          active ? "font-semibold text-admin-text" : "font-medium text-admin-textMuted group-hover:text-admin-text"
                        )}
                      >
                        {t.label}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </nav>

        <form
          id="loc-editor-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (isLastStep) {
              if (!primaryBlocked) onSubmit?.(primaryStatus);
            } else handleNext();
          }}
        >
          <div ref={scrollRef} className="space-y-6 pb-2">
            {totalIssues > 0 ? (
              <p role="alert" className="flex items-start gap-2 rounded-lg bg-admin-danger/[0.07] px-3 py-2.5 text-sm text-admin-danger">
                <AlertCircle size={16} className="mt-0.5 shrink-0" />
                <span>
                  Còn <b>{totalIssues}</b> trường cần sửa — xem dòng báo lỗi màu đỏ dưới từng ô.
                </span>
              </p>
            ) : null}

            {draftBanner ? (
              <div className="flex flex-wrap items-center gap-3 rounded-lg border border-admin-border bg-admin-canvas px-3 py-2.5 text-sm text-admin-text">
                <History size={16} className="shrink-0 text-admin-textMuted" />
                <span className="min-w-0 flex-1">
                  Có bản nháp tự lưu lúc{" "}
                  {new Date(draftBanner.savedAt).toLocaleString("vi-VN", { hour: "2-digit", minute: "2-digit", day: "2-digit", month: "2-digit" })}{" "}
                  chưa được lưu lên hệ thống.
                </span>
                <Button size="sm" variant="soft" onClick={draftBanner.onRestore}>
                  Khôi phục
                </Button>
                <Button size="sm" variant="ghost" onClick={draftBanner.onDiscard}>
                  Bỏ bản nháp
                </Button>
              </div>
            ) : null}

            {/* ───────────── Bước 1: Thông tin ───────────── */}
            {tab === "basic" && (
              <>
                <Section first title="Thông tin cơ bản" desc="Tên và phân loại giúp người dùng tìm thấy địa điểm trên bản đồ.">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FormField
                      label="Tên địa điểm"
                      required
                      name="name"
                      error={formErrors.name}
                      hint="Hiển thị trên bản đồ, danh sách và trang chi tiết."
                      className="sm:col-span-2"
                    >
                      <input
                        className={inputCls(formErrors.name)}
                        value={form.name}
                        onChange={(e) => setNameAndMaybeSlug(e.target.value)}
                        placeholder="VD: Thiền viện Trúc Lâm"
                        required
                      />
                    </FormField>

                    <FormField
                      label="Đường dẫn (slug)"
                      name="slug"
                      error={formErrors.slug}
                      hint="Tự tạo từ tên. Chỉ dùng chữ thường không dấu, số và dấu gạch ngang."
                      className="sm:col-span-2"
                      counter={
                        <button
                          type="button"
                          onClick={() => setForm((p) => ({ ...p, slug: slugifyLite(p.name || "") }))}
                          disabled={!String(form.name || "").trim()}
                          className="inline-flex items-center gap-1 text-xs font-medium text-admin-primary hover:underline disabled:opacity-40 disabled:hover:no-underline"
                        >
                          <RefreshCw size={12} />
                          Tạo lại từ tên
                        </button>
                      }
                    >
                      <div
                        className={cls(
                          "flex items-stretch overflow-hidden rounded-lg border bg-admin-surface transition focus-within:ring-4",
                          formErrors.slug
                            ? "border-admin-danger focus-within:ring-admin-danger/10"
                            : "border-admin-border hover:border-admin-textMuted/40 focus-within:border-admin-primary focus-within:ring-admin-primary/10"
                        )}
                      >
                        <span className="flex items-center border-r border-admin-border bg-admin-canvas px-3 text-sm text-admin-textMuted">/location/</span>
                        <input
                          className="min-w-0 flex-1 bg-transparent px-3 py-2 text-sm text-admin-text outline-none placeholder:text-admin-textMuted/60"
                          value={form.slug}
                          onChange={setter("slug")}
                          placeholder="tu-dong-tao-tu-ten"
                        />
                      </div>
                    </FormField>

                    <FormField label="Danh mục" required name="category" error={formErrors.category}>
                      <CategoryPicker
                        categories={categories}
                        value={form.category}
                        invalid={Boolean(formErrors.category)}
                        describe={describeCategory}
                        onChange={(id) => setForm((p) => ({ ...p, category: id }))}
                      />
                    </FormField>

                    <FormField
                      label="Loại hình"
                      optional
                      hint={
                        selectedCategorySlug
                          ? "Dùng cho bộ lọc loại hình và icon trên bản đồ."
                          : "Chọn danh mục trước để xem các loại hình phù hợp."
                      }
                    >
                      {subtypeGroup ? (
                        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Loại hình">
                          {[{ value: "auto", label: "Không chọn" }, ...subtypeGroup.items].map((it) => {
                            const active = selectedSubTypeValue === it.value;
                            return (
                              <button
                                key={it.value}
                                type="button"
                                role="radio"
                                aria-checked={active}
                                onClick={() => setForm((p) => ({ ...p, categorySlug: it.value === "auto" ? "" : it.value }))}
                                className={cls(
                                  "rounded-full border px-3 py-1.5 text-xs font-medium transition",
                                  active
                                    ? "border-admin-primary bg-admin-primary text-white"
                                    : "border-admin-border bg-admin-surface text-admin-text hover:border-admin-primary/50"
                                )}
                              >
                                {it.label}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="flex h-10 items-center rounded-lg border border-dashed border-admin-border bg-admin-canvas px-3 text-sm text-admin-textMuted">
                          {selectedCategorySlug ? "Danh mục này chưa có loại hình con" : "—"}
                        </div>
                      )}
                    </FormField>
                  </div>
                </Section>

                <Section title="Nội dung giới thiệu" desc="Phần người xem đọc đầu tiên ở popup bản đồ và trang chi tiết.">
                  <div className="space-y-4">
                    <FormField
                      label="Giới thiệu ngắn"
                      optional
                      name="shortIntro"
                      error={formErrors.shortIntro}
                      hint="Nên dài 120–200 ký tự, 1–2 câu nêu điểm đặc trưng nhất."
                      counter={<CharCounter count={String(form.shortIntro || "").length} max={300} />}
                    >
                      <textarea
                        className={inputCls(formErrors.shortIntro, "resize-y")}
                        rows={2}
                        value={form.shortIntro}
                        onChange={setter("shortIntro")}
                        placeholder="VD: Quán phở gia truyền hơn 30 năm, nước dùng thanh, đông khách buổi sáng."
                      />
                    </FormField>

                    <FormField
                      label="Mô tả chi tiết"
                      name="description"
                      error={formErrors.description}
                      hint="Bắt buộc từ 80 ký tự để xuất bản. Có thể lưu nháp khi chưa đủ."
                      counter={<CharCounter count={descLen} min={80} />}
                    >
                      <RichTextEditor
                        value={form.description || ""}
                        onChange={(html) => setForm((p) => ({ ...p, description: html }))}
                        invalid={Boolean(formErrors.description)}
                        placeholder="Giới thiệu lịch sử, không gian, món nổi bật, kinh nghiệm khi ghé…"
                      />
                    </FormField>
                  </div>
                </Section>

                <Section
                  title={mainSlug === "homestay-khach-san" ? "Liên hệ & giá phòng" : "Liên hệ, giá & giờ mở cửa"}
                  desc={selectedCategorySlug ? "Các trường hiển thị theo loại địa điểm đã chọn." : "Chọn danh mục để hiển thị đúng các trường cần thiết."}
                >
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <FormField label="Số điện thoại" optional name="phone" error={formErrors.phone}>
                      <div className="relative">
                        <Phone size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-admin-textMuted" />
                        <input
                          className={inputCls(formErrors.phone, "pl-9")}
                          value={form.phone}
                          onChange={setter("phone")}
                          placeholder="0909 123 456"
                          inputMode="tel"
                        />
                      </div>
                    </FormField>

                    <FormField label={priceLabel} optional name="priceMax" error={formErrors.priceMax} hint={priceHint}>
                      <input
                        type="text"
                        inputMode="numeric"
                        className={inputCls(formErrors.priceMax)}
                        value={form.priceRange ?? formatPriceRange(form.priceMin ?? form.price ?? 0, form.priceMax ?? 0)}
                        onChange={(e) => {
                          const raw = e.target.value;
                          // Cho phép nhập dạng: 10000 - 50000
                          const parts = raw.split("-");
                          const left = formatMoneyInput(parts[0] || "");
                          const right = parts.length > 1 ? formatMoneyInput(parts.slice(1).join("")) : "";
                          const formatted = parts.length > 1 ? `${left} - ${right}` : left;
                          const parsed = parsePriceRange(formatted);
                          setForm((prev) => ({
                            ...prev,
                            priceRange: formatted,
                            price: parsed.priceMin,
                            priceMin: parsed.priceMin,
                            priceMax: parsed.priceMax,
                          }));
                        }}
                        placeholder="VD: 30.000 - 80.000"
                      />
                    </FormField>

                    {showHours ? (
                      <>
                        <FormField label="Giờ mở cửa" optional name="openTime" error={formErrors.openTime} hint="Định dạng 24 giờ, VD 07:00.">
                          <div className="relative">
                            <Clock3 size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-admin-textMuted" />
                            <input className={inputCls(formErrors.openTime, "pl-9")} value={form.openTime} onChange={setter("openTime")} placeholder="07:00" />
                          </div>
                        </FormField>
                        <FormField label="Giờ đóng cửa" optional name="closeTime" error={formErrors.closeTime}>
                          <div className="relative">
                            <Clock3 size={15} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-admin-textMuted" />
                            <input className={inputCls(formErrors.closeTime, "pl-9")} value={form.closeTime} onChange={setter("closeTime")} placeholder="21:00" />
                          </div>
                        </FormField>
                      </>
                    ) : null}
                  </div>
                </Section>

                <Section title="Hiển thị trên bản đồ" desc="Nút liên kết ngoài và icon của địa điểm trong popup bản đồ.">
                  <div className="space-y-6">
                      <div className="space-y-4">
                        <p className="text-sm font-medium text-admin-text">Liên kết bên ngoài</p>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr,220px]">
                          <FormField
                            label="URL"
                            optional
                            hint={`Nút mở trang ngoài trong popup bản đồ (Website, Fanpage, Booking…). Đã có ${Number(form.homeLinkClicks || 0).toLocaleString("vi-VN")} lượt bấm.`}
                          >
                            <input className={iCls} value={form.homeLinkUrl || ""} onChange={setter("homeLinkUrl")} placeholder="https://…" inputMode="url" />
                          </FormField>
                          <FormField label="Tên nút" optional>
                            <input className={iCls} value={form.homeLinkLabel || ""} onChange={setter("homeLinkLabel")} placeholder="Đặt bàn" />
                          </FormField>
                        </div>
                      </div>

                      <div>
                        <p className="mb-3 text-sm font-medium text-admin-text">Icon trên bản đồ</p>
                        <div className="flex flex-wrap items-center gap-3">
                          <div
                            className="inline-flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border border-admin-border bg-admin-surface"
                            title={customMapIconUrl ? "Icon riêng" : mapIconLabel}
                          >
                            {customMapIconUrl ? (
                              <img src={customMapIconUrl} alt="" className="h-10 w-10 rounded-full object-contain" />
                            ) : mapIconPreview?.html ? (
                              <div
                                className="pointer-events-none"
                                style={{ transform: "scale(0.82)", transformOrigin: "center" }}
                                dangerouslySetInnerHTML={{ __html: mapIconPreview.html }}
                              />
                            ) : null}
                          </div>
                          <div className="min-w-0 flex-1 text-xs leading-5 text-admin-textMuted">
                            {customMapIconUrl ? "Đang dùng icon riêng." : `Tự động theo loại hình${mapIconLabel ? ` (${mapIconLabel})` : ""}.`}
                          </div>
                          <label className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg border border-admin-border bg-admin-surface px-3 text-xs font-semibold text-admin-text transition hover:bg-admin-muted">
                            <UploadCloud size={14} />
                            Tải icon riêng
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/webp,image/svg+xml,image/heic,image/heif,.heic,.heif"
                              className="hidden"
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                e.target.value = "";
                                if (!file) return;
                                // Giữ local; upload lên S3 khi Lưu
                                const normalized = await normalizeImageFileForUpload(file);
                                const objectUrl = URL.createObjectURL(normalized);
                                setForm((p) => ({
                                  ...p,
                                  mapIconAsset: {
                                    key: "",
                                    url: objectUrl,
                                    mimeType: normalized.type || "",
                                    size: Number(normalized.size || 0),
                                    alt: "",
                                    __localFile: normalized,
                                    __localName: normalized.name || "",
                                  },
                                }));
                              }}
                            />
                          </label>
                          {customMapIconUrl ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              icon={Trash2}
                              onClick={() => setForm((p) => ({ ...p, mapIconAsset: { key: "", url: "", mimeType: "", size: 0, alt: "" } }))}
                            >
                              Bỏ icon riêng
                            </Button>
                          ) : null}
                        </div>
                      </div>
                  </div>
                </Section>
              </>
            )}

            {/* ───────────── Bước 2: Media ───────────── */}
            {tab === "media" && (
              <>
                <Section
                  first
                  title="Ảnh đại diện"
                  desc="Ảnh chính trên bản đồ, danh sách và đầu trang chi tiết. Bắt buộc để xuất bản."
                >
                  <div data-field="image" className="grid grid-cols-1 gap-4 sm:grid-cols-[minmax(0,360px),1fr] sm:items-start">
                    <div className="aspect-video overflow-hidden rounded-xl">
                      <ImagePreview src={coverSrc} fallbackSrc={coverFallback} emptyText="Chưa có ảnh đại diện" heightCls="h-full aspect-video" />
                    </div>
                    <div className="space-y-3">
                      <div className="flex flex-wrap gap-2">
                        <Button variant={hasCover ? "secondary" : "primary"} icon={Upload} loading={uploading.cover} onClick={() => setDropCoverOpen(true)}>
                          {hasCover ? "Đổi ảnh" : "Tải ảnh lên"}
                        </Button>
                        <Button variant="secondary" icon={Library} onClick={() => setCoverPickerOpen(true)}>
                          Chọn từ thư viện
                        </Button>
                        {hasCover ? (
                          <Button variant="ghost" icon={Trash2} onClick={() => setDraftMedia((p) => ({ ...p, cover: null }))}>
                            Bỏ ảnh
                          </Button>
                        ) : null}
                      </div>
                      <p className="text-xs leading-5 text-admin-textMuted">
                        Tỉ lệ 16:9 · từ 1600 × 900 px · JPEG / PNG / WebP · tối đa 8 MB.
                        {draftMedia?.cover?.__localFile ? " Ảnh sẽ được tải lên khi bấm Lưu." : ""}
                      </p>
                    </div>
                  </div>
                </Section>

                <Section
                  title="Slider đầu trang"
                  desc="Ảnh chạy ở đầu trang chi tiết. Kéo thả để đổi thứ tự — ảnh đầu tiên hiển thị trước."
                  right={heroList.length ? <span className="text-xs text-admin-textMuted">{heroList.length} ảnh</span> : null}
                >
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                    {heroList.map((a, idx) => {
                      const src = resolveAssetDisplayUrl(a?.url, a?.key) || a?.url || "";
                      return (
                        <div
                          key={`${a?.key || a?.url || idx}`}
                          {...heroDrag.itemProps(idx)}
                          className={cls(
                            "group relative aspect-video cursor-grab overflow-hidden rounded-lg border bg-admin-canvas transition active:cursor-grabbing",
                            heroDrag.overIdx === idx && heroDrag.dragIdx !== idx ? "border-admin-primary ring-2 ring-admin-primary/30" : "border-admin-border",
                            heroDrag.dragIdx === idx && "opacity-40"
                          )}
                        >
                          {src ? <img src={src} alt="" draggable={false} className="h-full w-full object-cover" /> : null}
                          <span className="absolute left-1.5 top-1.5 rounded bg-black/55 px-1.5 text-[11px] font-semibold text-white">{idx + 1}</span>
                          {a?.__localFile ? (
                            <span className="absolute bottom-1.5 left-1.5 rounded bg-white/90 px-1.5 text-[10px] font-medium text-admin-text">Chờ tải lên</span>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => setForm((p) => ({ ...p, heroGallery: (p.heroGallery || []).filter((_, i) => i !== idx) }))}
                            className="absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-black/55 text-white opacity-0 transition hover:bg-admin-danger focus:opacity-100 group-hover:opacity-100"
                            aria-label={`Xoá ảnh ${idx + 1}`}
                          >
                            <X size={13} />
                          </button>
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      onClick={() => setDropHeroOpen(true)}
                      className="flex aspect-video flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-admin-border bg-admin-canvas text-xs font-medium text-admin-textMuted transition hover:border-admin-primary hover:text-admin-primary"
                    >
                      <Upload size={16} />
                      Tải lên
                    </button>
                    <button
                      type="button"
                      onClick={() => setHeroPickerOpen(true)}
                      className="flex aspect-video flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-admin-border bg-admin-canvas text-xs font-medium text-admin-textMuted transition hover:border-admin-primary hover:text-admin-primary"
                    >
                      <Library size={16} />
                      Thư viện
                    </button>
                  </div>
                </Section>

                <Section
                  title="Ảnh nội dung"
                  desc="Ảnh kèm chú thích theo thứ tự đọc trên trang chi tiết. Giữ biểu tượng ⋮⋮ để kéo đổi thứ tự."
                  right={
                    <Button size="sm" variant="secondary" icon={ImagePlus} onClick={addCollectionItem}>
                      Thêm ảnh
                    </Button>
                  }
                >
                  {(form.preservedMediaBlocks || []).length > 0 ? (
                    <p className="mb-3 rounded-lg border border-admin-border bg-admin-canvas px-3 py-2 text-xs leading-5 text-admin-textMuted">
                      Có {(form.preservedMediaBlocks || []).length} khối nội dung cũ (video/văn bản) không chỉnh sửa được ở đây — chúng được giữ nguyên khi lưu.
                    </p>
                  ) : null}
                  {mediaCount === 0 ? (
                    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-admin-border bg-admin-canvas px-6 py-10 text-center">
                      <Images size={22} className="text-admin-textMuted" />
                      <p className="mt-2 text-sm font-medium text-admin-text">Chưa có ảnh nội dung</p>
                      <p className="mt-1 max-w-sm text-xs leading-5 text-admin-textMuted">Thêm vài ảnh có chú thích để trang chi tiết hấp dẫn hơn.</p>
                      <Button className="mt-4" size="sm" variant="primary" icon={ImagePlus} onClick={addCollectionItem}>
                        Thêm ảnh đầu tiên
                      </Button>
                    </div>
                  ) : (
                    <ul className="divide-y divide-admin-border rounded-xl border border-admin-border">
                      {(form.mediaBlocks || []).map((b, idx) => {
                        const setBlock = (patch) =>
                          setForm((p) => {
                            const next = [...(p.mediaBlocks || [])];
                            next[idx] = { ...next[idx], ...patch, type: "image" };
                            return { ...p, mediaBlocks: next };
                          });
                        const remove = () => setForm((p) => ({ ...p, mediaBlocks: (p.mediaBlocks || []).filter((_, i) => i !== idx) }));
                        const move = (dir) => setForm((p) => ({ ...p, mediaBlocks: moveItem(p.mediaBlocks, idx, idx + dir) }));
                        const previewSrc = resolveAssetDisplayUrl(b?.image?.url, b?.image?.key);
                        const previewFallbackSrc = typeof b?.image?.url === "string" ? b.image.url.trim() : "";
                        const captionValue = b?.caption || "";
                        const hasImage = Boolean(String(b?.image?.url || "").trim());

                        return (
                          <li
                            key={idx}
                            {...blockDrag.itemProps(idx)}
                            className={cls(
                              "flex gap-3 p-3 transition",
                              blockDrag.overIdx === idx && blockDrag.dragIdx !== idx && "bg-admin-primaryLight",
                              blockDrag.dragIdx === idx && "opacity-40"
                            )}
                          >
                            <div className="flex shrink-0 flex-col items-center gap-1 pt-1">
                              <span
                                {...blockDrag.handleProps(idx)}
                                className="cursor-grab rounded p-1 text-admin-textMuted hover:bg-admin-muted active:cursor-grabbing"
                                title="Kéo để đổi thứ tự"
                                aria-hidden
                              >
                                <GripVertical size={16} />
                              </span>
                              <span className="text-xs font-semibold tabular-nums text-admin-textMuted">{idx + 1}</span>
                            </div>

                            <div className="w-28 shrink-0 sm:w-40">
                              <ImagePreview src={previewSrc} fallbackSrc={previewFallbackSrc} emptyText="Chưa có ảnh" heightCls="h-20 sm:h-24" />
                              <div className="mt-1.5 flex gap-1">
                                <button
                                  type="button"
                                  onClick={() => setDropContentIdx(idx)}
                                  disabled={collectionUploadingIdx === idx}
                                  className="flex-1 rounded-md border border-admin-border px-1.5 py-1 text-[11px] font-medium text-admin-text transition hover:bg-admin-muted disabled:opacity-50"
                                >
                                  {hasImage ? "Đổi" : "Tải lên"}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setCollectionPickerIdx(idx);
                                    setCollectionPickerOpen(true);
                                  }}
                                  className="flex-1 rounded-md border border-admin-border px-1.5 py-1 text-[11px] font-medium text-admin-text transition hover:bg-admin-muted"
                                >
                                  Thư viện
                                </button>
                              </div>
                            </div>

                            <div className="min-w-0 flex-1">
                              <div className="mb-1.5 flex items-center justify-between gap-2">
                                <span className="text-sm font-medium text-admin-text">Chú thích</span>
                                <CharCounter count={captionValue.length} max={CAPTION_MAX_LENGTH} />
                              </div>
                              <textarea
                                className={inputCls(captionValue.length > CAPTION_MAX_LENGTH, "resize-y")}
                                rows={3}
                                value={captionValue}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value.length === CAPTION_MAX_LENGTH + 1 && captionValue.length <= CAPTION_MAX_LENGTH) {
                                    toast?.(`Chú thích đã vượt giới hạn ${CAPTION_MAX_LENGTH} ký tự.`, "error");
                                  }
                                  setBlock({ caption: value });
                                }}
                                placeholder="Mô tả ngắn cho ảnh này…"
                              />
                            </div>

                            <div className="flex shrink-0 flex-col gap-0.5">
                              <IconBtn icon={ArrowUp} label="Lên" disabled={idx === 0} onClick={() => move(-1)} />
                              <IconBtn icon={ArrowDown} label="Xuống" disabled={idx === mediaCount - 1} onClick={() => move(1)} />
                              <IconBtn icon={Trash2} label="Xoá ảnh này" danger onClick={remove} />
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </Section>

                <Section
                  title="Video"
                  desc="Video được tải lên khi bấm Lưu. Thumbnail tự tạo từ video, có thể thay bằng ảnh riêng."
                  right={
                    <label className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg border border-admin-border bg-admin-surface px-3 text-xs font-semibold text-admin-text shadow-sm transition hover:bg-admin-muted">
                      <Video size={14} />
                      Thêm video
                      <input
                        type="file"
                        accept="video/*"
                        className="hidden"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          e.target.value = "";
                          if (!f) return;
                          if (!canAcceptVideoFile(f)) return;
                          await uploadVideosDraft([f], [], f.name.replace(/\.[^.]+$/, ""));
                        }}
                      />
                    </label>
                  }
                >
                  {uploading.videos ? (
                    <p className="mb-3 flex items-center gap-2 text-xs text-admin-textMuted">
                      <Loader2 size={13} className="animate-spin" /> Đang xử lý video…
                    </p>
                  ) : null}
                  {videoList.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-admin-border bg-admin-canvas px-6 py-8 text-center">
                      <PlayCircle size={22} className="mx-auto text-admin-textMuted" />
                      <p className="mt-2 text-sm font-medium text-admin-text">Chưa có video</p>
                      <p className="mt-1 text-xs text-admin-textMuted">Tối đa {MAX_VIDEO_MB}MB mỗi video.</p>
                    </div>
                  ) : (
                    <ul className="divide-y divide-admin-border rounded-xl border border-admin-border">
                      {videoList.map((v, idx) => {
                        const url = String(v?.url || "").trim();
                        const thumb = String(v?.thumbnail?.url || "").trim();
                        const pending = Boolean(v?.__localFile);
                        const remove = () => setDraftMedia((p) => ({ ...p, videos: (p.videos || []).filter((_, i) => i !== idx) }));
                        const move = (dir) => setDraftMedia((p) => ({ ...p, videos: moveItem(p.videos, idx, idx + dir) }));
                        return (
                          <li
                            key={`${v?.key || v?.url || idx}`}
                            {...videoDrag.itemProps(idx)}
                            className={cls(
                              "flex gap-3 p-3 transition",
                              videoDrag.overIdx === idx && videoDrag.dragIdx !== idx && "bg-admin-primaryLight",
                              videoDrag.dragIdx === idx && "opacity-40"
                            )}
                          >
                            <span
                              {...videoDrag.handleProps(idx)}
                              className="mt-1 h-fit cursor-grab rounded p-1 text-admin-textMuted hover:bg-admin-muted active:cursor-grabbing"
                              title="Kéo để đổi thứ tự"
                              aria-hidden
                            >
                              <GripVertical size={16} />
                            </span>

                            <div className="relative aspect-video w-32 shrink-0 overflow-hidden rounded-lg border border-admin-border bg-admin-canvas sm:w-44">
                              {thumb ? (
                                <img src={thumb} alt="" className="h-full w-full object-cover" />
                              ) : url ? (
                                <video key={url} src={url} className="h-full w-full object-cover" muted playsInline preload="metadata" />
                              ) : (
                                <div className="flex h-full items-center justify-center text-xs text-admin-textMuted">Không có xem trước</div>
                              )}
                              {url ? (
                                <a
                                  href={url}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="absolute inset-0 flex items-center justify-center bg-black/0 text-white/90 transition hover:bg-black/25"
                                  aria-label="Xem video"
                                >
                                  <PlayCircle size={26} />
                                </a>
                              ) : null}
                            </div>

                            <div className="min-w-0 flex-1 space-y-2">
                              <input
                                className={iCls}
                                value={String(v?.title || "")}
                                onChange={(e) => {
                                  const nextTitle = e.target.value;
                                  setDraftMedia((p) => {
                                    const next = [...(p.videos || [])];
                                    if (!next[idx]) return p;
                                    next[idx] = { ...next[idx], title: nextTitle };
                                    return { ...p, videos: next };
                                  });
                                }}
                                placeholder="Tiêu đề video"
                                aria-label="Tiêu đề video"
                              />
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs">
                                <span
                                  className={cls(
                                    "inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium",
                                    pending ? "bg-admin-warning/10 text-admin-warning" : "bg-admin-success/10 text-admin-success"
                                  )}
                                >
                                  {pending ? <CircleDot size={11} /> : <Check size={11} />}
                                  {pending ? "Chờ tải lên khi lưu" : "Đã tải lên"}
                                </span>
                                {v?.size ? <span className="text-admin-textMuted">{(Number(v.size) / 1024 / 1024).toFixed(1)} MB</span> : null}
                                <label className="cursor-pointer font-medium text-admin-primary hover:underline">
                                  Đổi video
                                  <input
                                    type="file"
                                    accept="video/*"
                                    className="hidden"
                                    onChange={async (e) => {
                                      const f = e.target.files?.[0];
                                      e.target.value = "";
                                      if (!f) return;
                                      if (!canAcceptVideoFile(f)) return;
                                      remove();
                                      await uploadVideosDraft([f], [], f.name.replace(/\.[^.]+$/, ""));
                                    }}
                                  />
                                </label>
                                <label className="cursor-pointer font-medium text-admin-primary hover:underline">
                                  Đổi thumbnail
                                  <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={async (e) => {
                                      const f = e.target.files?.[0];
                                      e.target.value = "";
                                      if (!f) return;
                                      const normalized = await normalizeImageFileForUpload(f);
                                      const objectUrl = URL.createObjectURL(normalized);
                                      setDraftMedia((p) => {
                                        const next = [...(p.videos || [])];
                                        if (!next[idx]) return p;
                                        next[idx] = {
                                          ...next[idx],
                                          thumbnail: {
                                            key: "",
                                            url: objectUrl,
                                            mimeType: normalized.type || "",
                                            size: Number(normalized.size || 0),
                                            alt: "",
                                            __localFile: normalized,
                                            __localName: normalized.name || "",
                                          },
                                        };
                                        return { ...p, videos: next };
                                      });
                                    }}
                                  />
                                </label>
                              </div>
                            </div>

                            <div className="flex shrink-0 flex-col gap-0.5">
                              <IconBtn icon={ArrowUp} label="Lên" disabled={idx === 0} onClick={() => move(-1)} />
                              <IconBtn icon={ArrowDown} label="Xuống" disabled={idx === videoList.length - 1} onClick={() => move(1)} />
                              <IconBtn icon={Trash2} label="Xoá video" danger onClick={remove} />
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </Section>

                <Section title="TikTok" desc="Dán link video TikTok để nhúng vào trang chi tiết.">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1fr,240px] sm:items-start">
                    <FormField label="Link TikTok" optional hint="VD: https://www.tiktok.com/@kenh/video/123… — hệ thống tự tạo mã nhúng.">
                      <input
                        className={iCls}
                        value={form.tiktokUrl}
                        onChange={(e) => setTikTokUrl(e.target.value)}
                        onBlur={(e) => {
                          const embed = toTikTokEmbedUrl(e.target.value);
                          if (!embed) return;
                          setForm((p) => ({ ...p, tiktokEmbed: embed }));
                        }}
                        placeholder="https://www.tiktok.com/@…"
                        inputMode="url"
                      />
                    </FormField>
                    {(() => {
                      const embed = String(form.tiktokEmbed || "").trim() || toTikTokEmbedUrl(form.tiktokUrl || "");
                      const src = tiktokPreviewSrc(embed);
                      if (!src) {
                        return String(form.tiktokUrl || "").trim() ? (
                          <p className="rounded-lg bg-admin-warning/10 px-3 py-2 text-xs text-admin-warning">
                            Chưa nhận ra video từ link này. Hãy dùng link dạng …/video/&lt;mã số&gt;.
                          </p>
                        ) : null;
                      }
                      return (
                        <div className="overflow-hidden rounded-lg border border-admin-border bg-black/5">
                          <div className="aspect-[9/12]">
                            <iframe
                              src={src}
                              title="TikTok preview"
                              className="h-full w-full border-0"
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </Section>
              </>
            )}

            {/* ───────────── Bước 3: Vị trí ───────────── */}
            {tab === "location" && (
              <>
                <Section first title="Tìm địa chỉ" desc="Chọn một gợi ý để tự đặt marker và điền phường, quận, tỉnh.">
                  <GeoapifyPlaceSearch
                    className={iCls}
                    value={geoQuery}
                    onChange={(text) => setGeoQuery(text)}
                    onSelect={(place) => {
                      const raw = place.raw || {};
                      const wardGuess = raw.suburb || raw.quarter || raw.neighbourhood || raw.village || "";
                      const districtGuess = raw.county || raw.municipality || raw.state_district || raw.district || "";
                      const cityGuess = raw.city || raw.town || raw.state || "";
                      setForm((prev) => ({
                        ...prev,
                        name: prev.name?.trim() ? prev.name : place.name || prev.name,
                        ward: wardGuess ? String(wardGuess) : prev.ward,
                        district: districtGuess ? String(districtGuess) : prev.district,
                        city: cityGuess ? String(cityGuess) : prev.city,
                        latitude: round6(place.latitude),
                        longitude: round6(place.longitude),
                        geoapifyPlaceId: place.placeId || prev.geoapifyPlaceId,
                      }));
                      setGeoQuery(place.address || place.name || "");
                      toast?.("Đã đặt vị trí và điền địa chỉ — kiểm tra lại bên dưới", "success");
                    }}
                  />
                </Section>

                <Section title="Vị trí trên bản đồ" desc="Bấm vào bản đồ hoặc kéo marker để chỉnh chính xác.">
                  <div className="space-y-4">
                    <MapPicker
                      lat={form.latitude}
                      lng={form.longitude}
                      onPick={(lat, lng) => setForm((p) => ({ ...p, latitude: round6(lat), longitude: round6(lng) }))}
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <FormField label="Vĩ độ" required name="latitude" error={formErrors.latitude}>
                        <input
                          type="text"
                          inputMode="decimal"
                          className={inputCls(formErrors.latitude, "tabular-nums")}
                          value={form.latitude}
                          onChange={setter("latitude")}
                          required
                        />
                      </FormField>
                      <FormField label="Kinh độ" required name="longitude" error={formErrors.longitude}>
                        <input
                          type="text"
                          inputMode="decimal"
                          className={inputCls(formErrors.longitude, "tabular-nums")}
                          value={form.longitude}
                          onChange={setter("longitude")}
                          required
                        />
                      </FormField>
                    </div>
                  </div>
                </Section>

                <Section title="Địa chỉ hành chính" desc="Tự điền khi chọn gợi ý ở trên — có thể sửa lại cho đúng.">
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                    <FormField label="Phường / Xã" optional>
                      <input className={iCls} value={form.ward} onChange={setter("ward")} placeholder="VD: Phường 1" />
                    </FormField>
                    <FormField label="Quận / Huyện" optional>
                      <input className={iCls} value={form.district} onChange={setter("district")} placeholder="VD: Đà Lạt" />
                    </FormField>
                    <FormField label="Tỉnh / Thành phố" optional>
                      <input className={iCls} value={form.city} onChange={setter("city")} placeholder="VD: Lâm Đồng" />
                    </FormField>
                  </div>
                </Section>
              </>
            )}

            {/* ───────────── Bước 4: Kiểm tra ───────────── */}
            {tab === "review" && (
              <LocationReviewStep
                form={form}
                draftMedia={draftMedia}
                categories={categories}
                validation={validation}
                goTo={goToStep}
                publishAs={alreadyPublished ? "published" : publishAs}
                subtypeLabel={subtypeLabel}
                canOpenLive={modalMode === "edit" && form.status === "published" && Boolean(String(form.slug || "").trim())}
              />
            )}
          </div>
        </form>
      </Modal>

      <MediaPickerModal
        open={coverPickerOpen}
        onClose={() => setCoverPickerOpen(false)}
        onSelect={(file) => {
          if (!file?.url) return;
          setDraftMedia((p) => ({
            ...p,
            cover: { key: file.key || "", url: file.url, mimeType: "", size: Number(file.size || 0), alt: "" },
          }));
          setCoverPickerOpen(false);
        }}
        title="Chọn ảnh đại diện"
        initialPrefix="locations/"
        accept="image"
      />

      <MediaPickerModal
        open={collectionPickerOpen}
        onClose={() => {
          setCollectionPickerOpen(false);
          setCollectionPickerIdx(-1);
        }}
        onSelect={(file) => {
          const asset = {
            key: file?.key || "",
            url: file?.url || "",
            mimeType: "",
            size: Number(file?.size || 0),
            alt: "",
          };
          if (!asset?.url) return;
          const idx = collectionPickerIdx;
          setForm((p) => {
            const next = [...(p.mediaBlocks || [])];
            if (!next[idx]) return p;
            next[idx] = { ...next[idx], type: "image", image: asset };
            return { ...p, mediaBlocks: next };
          });
          setCollectionPickerOpen(false);
          setCollectionPickerIdx(-1);
        }}
        title="Chọn ảnh từ thư viện"
        initialPrefix="locations/"
        accept="image"
      />

      <MediaPickerModal
        open={heroPickerOpen}
        onClose={() => setHeroPickerOpen(false)}
        onSelect={(file) => {
          const asset = {
            key: file?.key || "",
            url: file?.url || "",
            mimeType: "",
            size: Number(file?.size || 0),
            alt: "",
          };
          if (!asset?.url) return;
          setForm((p) => {
            const prev = Array.isArray(p.heroGallery) ? p.heroGallery : [];
            const key = asset.key || asset.url;
            if (key && prev.some((x) => (x?.key || x?.url) === key)) return p;
            return { ...p, heroGallery: [...prev, asset] };
          });
          setHeroPickerOpen(false);
        }}
        title="Chọn ảnh cho slider"
        initialPrefix="locations/"
        accept="image"
      />

      {/* ── ImageDropUpload modals ──────────────────────────── */}
      {dropCoverOpen && (
        <ImageDropUpload
          variant="cover"
          busy={uploading.cover}
          onPick={(file) => {
            setDropCoverOpen(false);
            uploadCoverDraft(file);
          }}
          onClose={() => setDropCoverOpen(false)}
        />
      )}

      {dropHeroOpen && (
        <ImageDropUpload
          variant="hero"
          onPick={async (fileArr) => {
            setDropHeroOpen(false);
            for (const f of fileArr) {
              await uploadHeroImageDraft(f);
            }
          }}
          onClose={() => setDropHeroOpen(false)}
        />
      )}

      {dropContentIdx >= 0 && (
        <ImageDropUpload
          variant="content"
          busy={collectionUploadingIdx === dropContentIdx}
          onPick={(file) => {
            const idx = dropContentIdx;
            setDropContentIdx(-1);
            uploadCollectionImageDraft(file, idx);
          }}
          onClose={() => setDropContentIdx(-1)}
        />
      )}
    </>
  );

  // Đưa ra cấp .admin-root để lớp phủ che cả header admin (vùng nội dung trang tạo stacking context riêng)
  return createPortal(tree, document.querySelector(".admin-root") || document.body);
}